/* eslint-disable react/prop-types */
import React, { useRef, useState, useEffect } from 'react';
import { ITooltipProps as DuiTooltipProps } from '@tencent/dui/lib/components/Tooltip/interface';
import DuiTooltip from '@tencent/dui/lib/components/Tooltip';

export interface ITooltipProps extends DuiTooltipProps {
    /**
     * 气泡被遮挡时自动调整位置， 默认 false;
     * NOTE: 当前不支持自适应 title 元素宽度，必须指定 `titleWidth` */
    autoAdjustOverflow?: boolean;
    /** 指定 title 元素展示需要的宽度，此宽度与具体元素大小无关，只在 autoAdjustOverflow = true 时用于计算空间 */
    titleWidth?: number;
    foreHide?: boolean;
}

type Placement = ITooltipProps['placement'];
/**
 * 动态调整 `placement`
 * @param tooltipDom
 * @param popupContentWidth 即 ITooltipProps['titleWidth'], 弹出内容所需宽度
 * @param placement 当前设定的 placement，高优先级
 */
export const adjustPlacement = (
    tooltipDom: HTMLDivElement,
    popupContentWidth: number,
    placement: Placement,
): Placement => {
    if (!tooltipDom) return placement;
    // TODO: 临时方案，只处理左右
    if (placement !== 'right' && placement !== 'left') return placement;

    const rect = tooltipDom.getBoundingClientRect();
    const distanceToRight = document.body.clientWidth - rect.right;
    const distanceToLeft = rect.left;
    console.debug(`distanceToRight ${distanceToRight} distanceToLeft ${distanceToLeft}`);

    const availableDirections: Placement[] = [];
    if (distanceToRight > popupContentWidth) availableDirections.push('right');
    if (distanceToLeft > popupContentWidth) availableDirections.push('left');

    // 任意方向都不行，则返回原输入
    if (availableDirections.length === 0) return placement;
    // 有可选项时，原输入优先级最高
    if (availableDirections.includes(placement)) return placement;

    return availableDirections[0];
};

/**
 * 拓展 DUI Tooltip, 支持气泡动态调整位置
 * NOTE: 只是临时方案，需要 push dui 支持该能力
 */
const Tooltip: React.FC<ITooltipProps> = (props) => {
    // 这里必须声明 textInput，这样 ref 才可以引用它
    const { autoAdjustOverflow, titleWidth, foreHide, ...tooltipProps } = props;

    const tooltipRef = useRef<DuiTooltip>(null);
    const [visible, setVisible] = useState<boolean | undefined>(props.visible);
    const [placement, setPlacement] = useState<Placement>(props.placement);

    const updatePlacement = () => {
        const tooltipDom = tooltipRef?.current?.rootRef?.current;
        if (tooltipDom && autoAdjustOverflow && titleWidth) {
            setPlacement(adjustPlacement(tooltipDom, titleWidth, props.placement));
        }
    };

    const onVisibleChange = (isVisible: boolean) => {
        updatePlacement();
        setVisible(isVisible);
    };

    // 若初始化时就可见，更新一次位置
    useEffect(() => {
        if (visible) updatePlacement();
    });

    return (
        <DuiTooltip
            {...tooltipProps}
            ref={tooltipRef}
            placement={placement}
            visible={foreHide ? false : visible}
            onVisibleChange={onVisibleChange}
        >
            {props.children}
        </DuiTooltip>
    );
};

export default Tooltip;
